"use client"

import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react"
import type { FilterSelection } from "@/schema/data-import"
import { Label, RadioGroup, RadioGroupItem } from "@ui/shad"
import { Loader2 } from "lucide-react"

import { useApplyFilters, useFetchDataImport, useFetchFilterOptions } from "@/hooks/api/data-imports"
import { MultiSelectInput } from "@/components/_ui/forms"
import { cn } from "@/components/_ui/utils"

interface FilterStepProps {
  dataImportId: string
  onNext: () => void
}

export interface FilterStepRef {
  submit: () => Promise<void>
  isPending: boolean
}

type ImportMode = "all" | "filtered"

export const FilterStep = forwardRef<FilterStepRef, FilterStepProps>(function FilterStep(
  { dataImportId, onNext },
  ref
) {
  const [importMode, setImportMode] = useState<ImportMode>("all")
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({})
  const [initialized, setInitialized] = useState(false)

  const { data: dataImport, isLoading: isLoadingImport } = useFetchDataImport(dataImportId)
  const { data: filterOptions, isLoading: isLoadingOptions } = useFetchFilterOptions(dataImportId)
  const applyFilters = useApplyFilters(dataImportId)

  // Initialize state from saved filters
  useEffect(() => {
    if (initialized || !dataImport) return

    setImportMode(dataImport.import_all_records ? "all" : "filtered")

    if (Array.isArray(dataImport.selected_filters)) {
      const filtersMap: Record<string, string[]> = {}
      dataImport.selected_filters.forEach((filter: FilterSelection) => {
        filtersMap[filter.filter_type] = filter.values
      })
      setSelectedFilters(filtersMap)
    }

    setInitialized(true)
  }, [dataImport, initialized])

  const isLoading = isLoadingImport || isLoadingOptions

  // Get filter types and labels from CRM-specific config
  const filterStepConfig = useMemo(
    () => dataImport?.filter_config.filter_step ?? [],
    [dataImport?.filter_config.filter_step]
  )

  const filterTypeLabels = useMemo(
    () => Object.fromEntries(filterStepConfig.map((f) => [f.type, f.label])),
    [filterStepConfig]
  )

  // Group filter options by type, only including types from filter_step config
  const filterGroups = useMemo(() => {
    if (!filterOptions || !dataImport) return {}
    const allowedTypes = filterStepConfig.map((f) => f.type)
    return filterOptions
      .filter((option) => allowedTypes.includes(option.filter_type))
      .reduce(
        (acc, option) => {
          if (!acc[option.filter_type]) {
            acc[option.filter_type] = []
          }
          acc[option.filter_type].push({
            value: option.filter_id,
            label: option.filter_label,
          })
          return acc
        },
        {} as Record<string, { value: string; label: string }[]>
      )
  }, [filterOptions, dataImport, filterStepConfig])

  const handleFilterChange = (filterType: string, values: string[]) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: values,
    }))
  }

  const handleApplyFilters = async () => {
    const importFilters: FilterSelection[] = Object.entries(selectedFilters)
      .filter(([, values]) => values.length > 0)
      .map(([filter_type, values]) => ({ filter_type, values }))

    await applyFilters.mutateAsync({
      import_mode: importMode,
      import_filters: importMode === "filtered" ? importFilters : undefined,
    })

    onNext()
  }

  useImperativeHandle(ref, () => ({
    submit: handleApplyFilters,
    isPending: applyFilters.isPending,
  }))

  if (isLoading) {
    return (
      <div className="mx-auto flex max-w-xl items-center justify-center py-12">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-xl">
      <h3 className="text-lg font-medium">Filter contacts</h3>
      <p className="text-muted-foreground mb-6">Choose which contacts to include in this import</p>

      <RadioGroup
        value={importMode}
        onValueChange={(value) => setImportMode(value as ImportMode)}
        className="space-y-0"
      >
        <label
          htmlFor="import-all"
          className="flex cursor-pointer items-center space-x-3 rounded-lg border border-dashed px-4 py-2"
        >
          <RadioGroupItem value="all" id="import-all" />
          <div>
            <Label htmlFor="import-all" className="cursor-pointer text-lg font-medium">
              Import all contacts
            </Label>
            <p className="text-muted-foreground text-sm">If you want the entire CRM imported, choose this option</p>
          </div>
        </label>
        <label
          htmlFor="import-filtered"
          className={cn(
            "flex cursor-pointer items-center space-x-3 border border-dashed px-4 py-2",
            importMode === "filtered" ? "rounded-t-lg border-b-0" : "rounded-lg"
          )}
        >
          <RadioGroupItem value="filtered" id="import-filtered" />
          <div>
            <Label htmlFor="import-filtered" className="cursor-pointer text-lg font-normal">
              Filter contacts prior to import
            </Label>
            <p className="text-muted-foreground text-sm">
              Only import contacts that match <strong>any</strong> of the following filters
            </p>
          </div>
        </label>
      </RadioGroup>

      {importMode === "filtered" && (
        <div className="space-y-2 rounded-lg rounded-t-none border border-t-0 border-dashed px-4 pb-4">
          {filterStepConfig.map(({ type: filterType }) => {
            const options = filterGroups[filterType]
            if (!options || options.length === 0) return null
            const label = filterTypeLabels[filterType] || filterType
            return (
              <MultiSelectInput
                key={filterType}
                id={`filter-${filterType}`}
                label={label}
                placeholder={`Select ${label.toLowerCase()}`}
                options={options}
                values={selectedFilters[filterType] || []}
                onChange={(values) => handleFilterChange(filterType, values)}
              />
            )
          })}

          {Object.keys(filterGroups).length === 0 && (
            <p className="text-muted-foreground text-sm italic">No filter options available for this import.</p>
          )}
        </div>
      )}
    </div>
  )
})
